import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  getOperatingHours,
  getOperatingHoursProjection,
  previewOperatingHours,
  setOperatingHours,
  type FacilityOperatingHoursChangePreview,
} from "@/libs/facilityApi";
import { facilityApprovalReadinessQueryKey } from "./facility-approval";
import { OperatingHoursEditor } from "./operating-hours-editor";
import {
  emptyOperatingHoursDraft,
  operatingHoursDraftFromSnapshot,
  operatingHoursDraftToCommand,
  validateOperatingHoursDraft,
  type OperatingHoursDraft,
} from "./operating-hours";

function localToday(timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const value = (kind: string) => parts.find((part) => part.type === kind)?.value;
  const year = value("year");
  const month = value("month");
  const day = value("day");
  return year && month && day ? `${year}-${month}-${day}` : "";
}

function formatLocalTime(instant: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(instant));
}

function formatLocalDateTime(instant: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(instant));
}

function projectionSourceLabel(kind: string): string {
  switch (kind) {
    case "schedule":
      return "Weekly schedule";
    case "date_override":
      return "Date exception";
    case "closure":
      return "Closure period";
    case "schedule_gap":
      return "No effective schedule";
    default:
      return "Not configured";
  }
}

function ConflictList({
  preview,
  timezone,
}: {
  preview: FacilityOperatingHoursChangePreview;
  timezone: string;
}) {
  if (preview.conflicts.length === 0) return null;
  return (
    <Alert severity="warning">
      <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
        {preview.conflicts.map((conflict) => (
          <Typography component="li" key={`${conflict.court_id}-${conflict.starts_at}-${conflict.occupancy_type}`} variant="body2">
            {conflict.is_recurring
              ? `${conflict.court_name}: recurring ${conflict.occupancy_type} must be resolved before these hours can take effect.`
              : `${conflict.court_name}: ${conflict.occupancy_type} from ${formatLocalDateTime(conflict.starts_at, timezone)} to ${formatLocalDateTime(conflict.ends_at, timezone)}. Resolve it in ${conflict.remediation.replace("_", " ")}.`}
          </Typography>
        ))}
      </Stack>
    </Alert>
  );
}

export function OperatingHoursPanel({
  facilityId,
  canManage,
}: {
  facilityId: string;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const snapshotQuery = useQuery({
    queryKey: ["facility", facilityId, "operating-hours"],
    queryFn: () => getOperatingHours(facilityId),
    enabled: Boolean(facilityId),
  });
  const [draft, setDraft] = useState<OperatingHoursDraft>(() =>
    emptyOperatingHoursDraft(),
  );
  const [dirty, setDirty] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [preview, setPreview] = useState<FacilityOperatingHoursChangePreview | null>(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const snapshot = snapshotQuery.data;
    if (!snapshot || dirty) return;
    const today = localToday(snapshot.timezone);
    const emptyDraft = emptyOperatingHoursDraft(
      snapshot.reservation_state_revision,
    );
    emptyDraft.schedules[0].effectiveFrom = today;
    setDraft(
      snapshot.schedules.length > 0
        ? operatingHoursDraftFromSnapshot(snapshot)
        : emptyDraft,
    );
    setSelectedDate((current) => current || today);
  }, [dirty, snapshotQuery.data]);

  const projectionQuery = useQuery({
    queryKey: ["facility", facilityId, "operating-hours", "projection", selectedDate],
    queryFn: () => getOperatingHoursProjection(facilityId, selectedDate),
    enabled: Boolean(facilityId) && Boolean(selectedDate),
  });

  function updateDraft(next: OperatingHoursDraft) {
    setDraft(next);
    setDirty(true);
    setPreview(null);
    setValidationError(null);
    setRequestError(null);
  }

  async function checkConflicts() {
    const validation = validateOperatingHoursDraft(draft);
    if (validation) {
      setValidationError(validation);
      return;
    }
    setChecking(true);
    setValidationError(null);
    setRequestError(null);
    try {
      setPreview(
        await previewOperatingHours(
          facilityId,
          operatingHoursDraftToCommand(draft),
        ),
      );
    } catch (error) {
      setRequestError(
        error instanceof Error
          ? error.message
          : "Could not check operating-hours conflicts. Reload and try again.",
      );
    } finally {
      setChecking(false);
    }
  }

  async function save() {
    if (!preview?.can_apply) return;
    setSaving(true);
    setRequestError(null);
    try {
      const saved = await setOperatingHours(
        facilityId,
        operatingHoursDraftToCommand(draft),
      );
      queryClient.setQueryData(
        ["facility", facilityId, "operating-hours"],
        saved,
      );
      setDraft(operatingHoursDraftFromSnapshot(saved));
      setDirty(false);
      setPreview(null);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["facility", facilityId, "operating-hours"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["facility", facilityId, "operating-hours", "projection"],
        }),
        queryClient.invalidateQueries({
          queryKey: facilityApprovalReadinessQueryKey(facilityId),
        }),
        queryClient.invalidateQueries({ queryKey: ["booking", "availability", facilityId] }),
      ]);
    } catch (error) {
      setRequestError(
        error instanceof Error
          ? error.message
          : "Could not save operating hours. Reload and try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  if (snapshotQuery.isLoading) {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", py: 2 }}>
        <CircularProgress size={18} />
        <Typography color="text.secondary" variant="body2">
          Loading operating hours…
        </Typography>
      </Stack>
    );
  }
  if (snapshotQuery.isError) {
    return <Alert severity="error">Could not load operating hours.</Alert>;
  }

  const projection = projectionQuery.data;
  const disabled = !canManage || checking || saving;
  return (
    <Stack spacing={3}>
      <OperatingHoursEditor disabled={disabled} onChange={updateDraft} value={draft} />

      <Divider />
      <Stack spacing={1.25}>
        <Typography component="h3" sx={{ fontWeight: 800 }} variant="subtitle1">
          Check a date
        </Typography>
        <Box sx={{ maxWidth: 240 }}>
          <TextField
            label="Facility-local date"
            onChange={(event) => setSelectedDate(event.target.value)}
            size="small"
            type="date"
            value={selectedDate}
          />
        </Box>
        {projectionQuery.isLoading ? (
          <Typography color="text.secondary" variant="body2">
            Resolving this date…
          </Typography>
        ) : projectionQuery.isError ? (
          <Alert severity="error">Could not resolve this operating date.</Alert>
        ) : projection ? (
          <Stack spacing={0.5}>
            <Typography color="text.secondary" variant="body2">
              Saved policy for this facility-local date.
            </Typography>
            <Typography variant="body2">
              Source: <strong>{projectionSourceLabel(projection.effective_source_kind)}</strong>
            </Typography>
            {projection.intervals.length > 0 ? (
              projection.intervals.map((interval) => (
                <Typography color="text.secondary" key={`${interval.opens_at}-${interval.closes_at}`} variant="body2">
                  {formatLocalTime(interval.opens_at, projection.timezone)}–{formatLocalTime(interval.closes_at, projection.timezone)}
                </Typography>
              ))
            ) : (
              <Typography color="text.secondary" variant="body2">
                Closed on this date.
              </Typography>
            )}
          </Stack>
        ) : null}
      </Stack>

      {validationError ? <Alert severity="error">{validationError}</Alert> : null}
      {requestError ? <Alert severity="error">{requestError}</Alert> : null}
      {preview ? (
        <Stack spacing={1.5}>
          {preview.can_apply ? (
            <Alert severity="success">No future court claims conflict with this change.</Alert>
          ) : (
            <ConflictList
              preview={preview}
              timezone={snapshotQuery.data?.timezone ?? "America/Los_Angeles"}
            />
          )}
        </Stack>
      ) : null}
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Button
          disabled={disabled || !dirty}
          onClick={checkConflicts}
          size="small"
          type="button"
          variant="outlined"
        >
          {checking ? "Checking…" : "Check conflicts"}
        </Button>
        {preview?.can_apply ? (
          <Button
            disabled={disabled}
            onClick={save}
            size="small"
            type="button"
            variant="contained"
          >
            {saving ? "Saving…" : "Save operating hours"}
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}
